import { useSelector, useSetProductEdition, useSetProductUnitCount } from '../../../context/hooks';
import * as selectors from '../../../context/selectors';
import { formatPrice } from '../../strings';

import type { Edition, ProductKey } from '../../../calculator/types';

/**
 * Context-bound state and handlers for a product card. This is plumbing only —
 * product display text (name, unit label, edition keys/labels) lives in each
 * per-product card component, not here.
 */
export interface ProductCardContext {
	edition: Edition;
	onEditionChange: (edition: Edition) => void;
	unitCount: number;
	onUnitCountChange: (unitCount: number) => void;
	/** Max unit count, sourced from the product config's `signupLimit`. */
	maxUnitCount: number;
	isFree: boolean;
	formattedPerUnitPerMonth?: string;
}

export const useProductCardData = (productKey: ProductKey): ProductCardContext => {
	const edition = useSelector((ctx) => selectors.getProductEdition(ctx, productKey));
	const unitCount = useSelector((ctx) => selectors.getProductUnitCount(ctx, productKey));
	const maxUnitCount = useSelector((ctx) => selectors.getProductMaxUnitCount(ctx, productKey));
	const perUnitPerMonth = useSelector((ctx) =>
		selectors.getProductPerUnitPerMonth(ctx, productKey),
	);
	const currency = useSelector(selectors.getCurrencyIsoCode);
	const locale = useSelector(selectors.getNumberFormatLocale);

	const setEdition = useSetProductEdition();
	const setUnitCount = useSetProductUnitCount();

	const formattedPerUnitPerMonth =
		perUnitPerMonth === undefined
			? undefined
			: formatPrice(perUnitPerMonth, {
					currency,
					locale,
					fractionDigits: 2,
				});

	return {
		edition,
		onEditionChange: (next) => setEdition(productKey, next),
		unitCount,
		onUnitCountChange: (next) => setUnitCount(productKey, next),
		maxUnitCount,
		isFree: edition === 'free',
		formattedPerUnitPerMonth,
	};
};
